import React, {
  useState, useRef, useEffect, useLayoutEffect, useMemo,
} from 'react';
import {createPortal} from 'react-dom';
import classnames from 'classnames';
import {SecurityTypes} from '@qep/types/src';
import {type Product} from '../../utils/Product';
import {useSymbolSearch} from '../../hooks/use-symbol-search';
import {useDebounce} from '../../hooks/use-debounce';
import {useExchangeSymbols} from '../../hooks/use-exchange-symbols';
import {TruncatedText} from './TruncatedText';
import {Spinner} from './Spinner';
import {OptionsSelectPopover} from './OptionsSelectPopover';
import './SymbolPicker.scss';

export type SymbolPickerFilter = 'Future' | 'Option' | 'Futures Spread';

export type SymbolPickerMode = 'single' | 'multi';

export type SymbolPickerFooterContext = {
  selected: Product[];
  clear: () => void;
  apply: () => void;
  close: () => void;
};

/** One-shot command that opens the picker with caller-selected search scope and focus. */
export type SymbolPickerSearchRequest = {
  filter: SymbolPickerFilter;
  requestId: number;
  searchTerm: string;
  selectedExchanges?: string[];
};

export type SymbolPickerProps = {
  /**
   * Selection mode. `single` fires onChange with a single-element array and
   * closes the dropdown on click. `multi` accumulates a local selection that
   * must be committed explicitly (via `renderFooter`); when `renderFooter` is
   * not provided, each row click fires onChange with a single-element array
   * and keeps the dropdown open (useful for "add to watchlist" flows).
   */
  mode: SymbolPickerMode;

  /** Invoked whenever the effective selection changes. */
  onChange: (products: Product[]) => void;

  /** Optional controlled list of currently selected products (multi mode). */
  value?: Product[];

  /** Placeholder text for the search input. */
  placeholder?: string;

  /**
   * Which filter tabs are enabled. Tabs not in this list are rendered but
   * disabled (matching the original order-entry picker look-and-feel).
   * Defaults to all four tabs.
   */
  enabledFilters?: SymbolPickerFilter[];

  /** Which filter tab is initially active and becomes active when this value changes to an enabled filter. */
  defaultFilter?: SymbolPickerFilter;

  /** Whether backend search results are limited to tradable instruments. Defaults to true. */
  tradeable?: boolean;

  /** Optional one-shot command used when a parent workflow must guide the next search. */
  searchRequest?: SymbolPickerSearchRequest;

  /**
   * Optional render-prop footer used by callers that want an explicit apply
   * step (order-entry algo mode). When provided, clicking rows only toggles
   * the local selection and the footer is rendered below the results.
   */
  renderFooter?: (context: SymbolPickerFooterContext) => React.ReactNode;

  /** Extra class name applied to the root wrapper. */
  className?: string;
};

type SortKey = 'symbol' | 'normalizedSymbol' | 'segmentMic' | 'description' | 'secType' | undefined;

const ALL_FILTERS: SymbolPickerFilter[] = ['Future', 'Option', 'Futures Spread'];

/**
 * Icon components used in the column header sort buttons. The picker doesn't
 * use text labels in the header row (by design) so these icons carry the
 * entire meaning of the sort state.
 */
function SortIcon() {
  return (
    <svg fill='currentColor' viewBox='0 0 24 24'>
      <path d='M3 18h6v-2H3v2zM3 6v2h18V6H3zm0 7h12v-2H3v2z' />
    </svg>
  );
}

function AscendingIcon() {
  return (
    <svg fill='currentColor' viewBox='0 0 24 24'>
      <path d='M7 14l5-5 5 5z' />
    </svg>
  );
}

function DescendingIcon() {
  return (
    <svg fill='currentColor' viewBox='0 0 24 24'>
      <path d='M7 10l5 5 5-5z' />
    </svg>
  );
}

type SortConfig = {key: SortKey; direction: 'ascending' | 'descending'};

function SortButton({
  keyName, sortConfig, setSortConfig,
}: {
  keyName: Exclude<SortKey, undefined>;
  sortConfig: SortConfig;
  setSortConfig: (sortConfig: SortConfig) => void;
}) {
  return (
    <button
      type='button'
      className='sp-sort-btn'
      onClick={() => {
        setSortConfig({
          key: sortConfig.key === keyName && sortConfig.direction === 'descending' ? undefined : keyName,
          direction: sortConfig.key === keyName && sortConfig.direction === 'ascending'
            ? 'descending'
            : 'ascending',
        });
      }}
    >
      {sortConfig.key === keyName && sortConfig.direction === 'ascending'
        ? <AscendingIcon />
        : (sortConfig.key === keyName && sortConfig.direction === 'descending'
          ? <DescendingIcon />
          : <SortIcon />)}
    </button>
  );
}

/**
 * Map the currently active filter tab to the list of security types that the
 * backend symbol lookup should filter by. Each tab maps 1:1 to its
 * `SecurityTypes` enum value; there is intentionally no "All" tab here - the
 * legacy SymbolSelect didn't expose one and the current product spec
 * matches that behavior.
 */
function securityTypesForFilter(filter: SymbolPickerFilter): SecurityTypes[] {
  switch (filter) {
    case 'Future': {
      return [SecurityTypes.Future];
    }

    case 'Option': {
      return [SecurityTypes.Option];
    }

    case 'Futures Spread':
    default: {
      return [SecurityTypes.FuturesSpread];
    }
  }
}

/**
 * Human-readable label for the type column. Matches the Fut/Opt/Spd convention
 * that trading blotters use across the app.
 */
function secTypeLabel(secType: SecurityTypes | undefined): string {
  if (secType === SecurityTypes.Future) {
    return 'Fut';
  }

  if (secType === SecurityTypes.Option) {
    return 'Opt';
  }

  return 'Spd';
}

/** Pixel box for the portaled `.sp-dropdown`; computed once per open/resize. */
export type SymbolPickerPanelLayout = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const PANEL_DEFAULT_WIDTH_PX = 500;
const PANEL_LAYOUT_GAP_PX = 8;

/**
 * Computes dropdown size and `position: fixed` coordinates for every host app.
 * Uses most of the viewport height (capped by space below the anchor), flips
 * above the input when that yields more room, clamps into the window, and
 * shrinks width on narrow frames so the panel stays usable.
 */
export function computeSymbolPickerPanelLayout(
  anchor: HTMLElement,
  options: {
    defaultWidthPx?: number;
    minHeightPx?: number;
    viewportHeightFraction?: number;
    gap?: number;
  } = {},
): SymbolPickerPanelLayout {
  const {
    defaultWidthPx = PANEL_DEFAULT_WIDTH_PX,
    minHeightPx = 280,
    viewportHeightFraction = 0.88,
    gap = PANEL_LAYOUT_GAP_PX,
  } = options;

  const win = anchor.ownerDocument.defaultView ?? globalThis;
  const vw = win.innerWidth;
  const vh = win.innerHeight;
  const {
    bottom: anchorBottom,
    top: anchorTop,
    left: anchorLeft,
  } = anchor.getBoundingClientRect();

  const widthPx = Math.min(
    defaultWidthPx,
    Math.max(160, vw - 2 * gap),
  );

  const spaceBelow = Math.max(0, vh - anchorBottom - gap);
  const spaceAbove = Math.max(0, anchorTop - gap);
  const heightCeiling = Math.floor(vh * viewportHeightFraction);

  let heightPx = Math.min(heightCeiling, spaceBelow);
  let top = anchorBottom + gap;

  if (heightPx < minHeightPx && spaceAbove >= minHeightPx && spaceAbove > spaceBelow) {
    heightPx = Math.min(heightCeiling, spaceAbove);
    top = anchorTop - heightPx - gap;
  } else if (heightPx < minHeightPx) {
    heightPx = Math.min(minHeightPx, Math.max(80, spaceBelow));
  }

  heightPx = Math.max(60, Math.min(heightPx, heightCeiling));
  top = Math.max(gap, Math.min(top, vh - heightPx - gap));

  let left = anchorLeft;
  if (left + widthPx + gap > vw) {
    left = Math.max(gap, vw - widthPx - gap);
  }

  return {
    top,
    left,
    width: widthPx,
    height: heightPx,
  };
}

/**
 * Shared symbol picker used across order-entry, ticker grid and order book.
 *
 * Renders a search box that, on focus, opens a dropdown (panel portaled to
 * `document.body` so host `overflow` / transforms do not clip it) with a
 * filter bar, an exchanges multi-select, sortable results, and an optional
 * footer (rendered via the `renderFooter` prop). The dropdown auto-closes
 * on outside click, except for clicks inside the exchanges popover.
 */
/* eslint-disable complexity -- picker composes hooks, portal, filter bar, results, and optional footer */
export const SymbolPicker: React.FC<SymbolPickerProps> = ({
  mode,
  onChange,
  value,
  placeholder = 'Search for symbols (e.g. ZC, Corn)',
  enabledFilters = ALL_FILTERS,
  defaultFilter = 'Future',
  tradeable = true,
  searchRequest,
  renderFooter,
  className,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<SymbolPickerFilter>(defaultFilter);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([]);
  const [isExchangesOpen, setIsExchangesOpen] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState({top: 0, left: 0});
  const [panelLayout, setPanelLayout] = useState<SymbolPickerPanelLayout | undefined>();
  const [localSelection, setLocalSelection] = useState<Product[]>(value ?? []);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: undefined,
    direction: 'ascending',
  });
  const {data: exchanges = []} = useExchangeSymbols();

  const searchRef = useRef<HTMLDivElement>(null);
  /** Search input node; refocused after mouse tab changes so typing continues in the picker. */
  const searchInputRef = useRef<HTMLInputElement>(null);
  /** Portaled dropdown root; kept outside `searchRef` so outside-click logic still treats it as inside the picker. */
  const dropdownRef = useRef<HTMLDivElement>(null);
  const exchangesBtnRef = useRef<HTMLButtonElement>(null);
  const handledSearchRequestIdRef = useRef<number>();
  /** Last default prop observed, independent of whether that default could be applied. */
  const previousDefaultFilterRef = useRef(defaultFilter);
  /** Most recent changed default waiting for its filter to become enabled. */
  const pendingDefaultFilterRef = useRef<SymbolPickerFilter>();

  // Initialize the exchanges filter to "all selected" once we know the list.
  useEffect(() => {
    if (exchanges.length > 0) {
      setSelectedExchanges(previous => (previous.length === 0 ? exchanges : previous));
    }
  }, [exchanges]);

  // A request id makes repeated root searches deterministic without turning normal typing
  // into a controlled input. Focus fires the existing open behavior after the term and scope
  // have been staged, including when a modal has just released focus.
  useEffect(() => {
    if (!searchRequest || handledSearchRequestIdRef.current === searchRequest.requestId) {
      return;
    }

    handledSearchRequestIdRef.current = searchRequest.requestId;
    setSearchTerm(searchRequest.searchTerm);
    setFilterType(searchRequest.filter);
    if (searchRequest.selectedExchanges) {
      setSelectedExchanges(searchRequest.selectedExchanges);
    }

    setIsSearching(true);
    searchInputRef.current?.focus();
  }, [searchRequest]);

  // Keep the local selection mirror in sync with controlled `value` changes.
  useEffect(() => {
    if (value) {
      setLocalSelection(value);
    }
  }, [value]);

  const securityTypes = useMemo(
    () => securityTypesForFilter(filterType),
    [filterType],
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  const {isLoading: isSymbolSearchLoading, data: symbolSearch} = useSymbolSearch(
    debouncedSearchTerm,
    securityTypes,
    selectedExchanges,
    tradeable,
  );

  /**
   * Applies enabled mode-driven defaults without resetting explicit tab choices
   * during unrelated rerenders. Changed defaults stay pending until enabled.
   * If the active filter becomes disabled, such as an Order Entry mode turning
   * off Option, the first enabled filter remains the safe fallback.
   */
  useEffect(() => {
    const defaultFilterChanged = previousDefaultFilterRef.current !== defaultFilter;
    if (defaultFilterChanged) {
      previousDefaultFilterRef.current = defaultFilter;
      pendingDefaultFilterRef.current = defaultFilter;
    }

    const pendingDefaultFilter = pendingDefaultFilterRef.current;
    if (pendingDefaultFilter && enabledFilters.includes(pendingDefaultFilter)) {
      pendingDefaultFilterRef.current = undefined;
      setFilterType(pendingDefaultFilter);
      return;
    }

    const firstEnabledFilter = enabledFilters[0];
    if (!enabledFilters.includes(filterType) && firstEnabledFilter) {
      setFilterType(firstEnabledFilter);
    }
  }, [defaultFilter, enabledFilters, filterType]);

  // Single layout path for all hosts: panel height/width/top/left from viewport +
  // anchor rect (see `computeSymbolPickerPanelLayout`). Cleared when closed so
  // the next open recomputes from a fresh measure.
  useLayoutEffect(() => {
    if (!isSearching) {
      setPanelLayout(undefined);
      return;
    }

    if (!searchRef.current) {
      return;
    }

    const layout = () => {
      if (!searchRef.current) {
        return;
      }

      setPanelLayout(computeSymbolPickerPanelLayout(searchRef.current));
    };

    layout();
    const win = searchRef.current.ownerDocument.defaultView ?? globalThis;
    win.addEventListener('resize', layout);
    return () => {
      win.removeEventListener('resize', layout);
    };
  }, [isSearching]);

  // Close the dropdown when the user clicks outside it, with an exception for
  // the exchanges popover which lives in a separate portal element. The
  // dropdown panel is portaled to `document.body`, so it is not under
  // `searchRef` in the DOM tree and must be checked explicitly.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insidePicker = Boolean(searchRef.current?.contains(target) || dropdownRef.current?.contains(target));
      if (insidePicker) {
        return;
      }

      const exchangePopover = document.querySelector('#sp-exchanges-popover');
      if (exchangePopover?.contains(target)) {
        return;
      }

      setIsSearching(false);
      setIsExchangesOpen(false);
      setLocalSelection(value ?? []);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value]);

  const handleExchangesToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (exchangesBtnRef.current) {
      const rect = exchangesBtnRef.current.getBoundingClientRect();
      const popoverWidth = 176;
      const viewportWidth = window.innerWidth;
      const padding = 8;

      let left = rect.left + window.scrollX - 40;
      if (left + popoverWidth + padding > viewportWidth) {
        left = Math.max(padding, viewportWidth - popoverWidth - padding);
      }

      setPopoverCoords({
        top: rect.bottom + window.scrollY + 8,
        left,
      });
      setIsExchangesOpen(!isExchangesOpen);
    }
  };

  const toggleExchange = (exchange: string) => {
    setSelectedExchanges(previous =>
      previous.includes(exchange)
        ? previous.filter(current => current !== exchange)
        : [...previous, exchange]);
  };

  const toggleLocalSelection = (product: Product) => {
    setLocalSelection(previous =>
      previous.includes(product)
        ? previous.filter(current => current !== product)
        : [...previous, product]);
  };

  const hasExplicitApply = Boolean(renderFooter);
  const isMultiMode = mode === 'multi';

  const handleRowClick = (product: Product) => {
    if (hasExplicitApply && isMultiMode) {
      toggleLocalSelection(product);
      return;
    }

    if (isMultiMode) {
      // Multi mode without an explicit apply footer: each click fires onChange
      // with the single newly-picked product and keeps the dropdown open so
      // the user can continue picking (e.g. adding watchlist symbols).
      onChange([product]);
      return;
    }

    onChange([product]);
    setIsSearching(false);
  };

  const applySelection = () => {
    onChange(localSelection);
    // When used uncontrolled, reset the local multi-select after applying so
    // the next opening of the dropdown starts fresh (e.g. ticker grid's
    // "add to watchlist" flow shouldn't remember the previously-applied
    // symbols). Controlled callers are expected to update `value` instead.
    if (value === undefined) {
      setLocalSelection([]);
    }

    setIsSearching(false);
  };

  const clearSelection = () => {
    setLocalSelection([]);
  };

  const closeDropdown = () => {
    setIsSearching(false);
  };

  const sortedSymbols = useMemo(() => {
    const items = [...(symbolSearch ?? [])];
    if (sortConfig.key === undefined) {
      return items;
    }

    return items.sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      if (bValue === undefined) {
        return -1;
      }

      if (aValue === undefined) {
        return 1;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }

      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }

      return 0;
    });
  }, [symbolSearch, sortConfig]);

  const showSelectionStatus = hasExplicitApply && isMultiMode;
  const tableSpan = showSelectionStatus ? 6 : 5;

  return (
    <div className={classnames('sp-root', className)} ref={searchRef}>
      <div className='sp-input-wrap'>
        <span className='sp-input-icon'>
          <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
          </svg>
        </span>
        <input
          ref={searchInputRef}
          type='text'
          aria-label={placeholder}
          className='sp-input'
          placeholder={placeholder}
          value={searchTerm}
          onChange={event => {
            setSearchTerm(event.target.value);
          }}
          onFocus={() => {
            setIsSearching(true);
          }}
        />
      </div>

      {isSearching && panelLayout && typeof document !== 'undefined' && createPortal(
        <div
          className={classnames('sp-root', className)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            margin: 0,
            padding: 0,
            border: 'none',
            background: 'transparent',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 10050,
          }}
        >
          <div
            ref={dropdownRef}
            className={classnames('sp-dropdown', {
              'sp-dropdown--has-apply-footer': hasExplicitApply && isMultiMode,
            })}
            style={{
              pointerEvents: 'auto',
              top: `${panelLayout.top}px`,
              left: `${panelLayout.left}px`,
              width: `${panelLayout.width}px`,
              height: `${panelLayout.height}px`,
              maxHeight: `${panelLayout.height}px`,
            }}
          >
            <div className='sp-filter-bar'>
              {ALL_FILTERS.map(filter => {
                const isDisabled = !enabledFilters.includes(filter);
                const isActive = filterType === filter && !isDisabled;

                return (
                  <button
                    type='button'
                    key={filter}
                    disabled={isDisabled}
                    onMouseDown={event => {
                      event.preventDefault();
                    }}
                    onClick={event => {
                      setFilterType(filter);
                      if (event.detail > 0) {
                        searchInputRef.current?.focus();
                      }
                    }}
                    className={classnames('sp-filter-tab', {
                      'sp-filter-tab--active': isActive,
                      'sp-filter-tab--disabled': isDisabled,
                    })}
                  >
                    {filter}
                  </button>
                );
              })}
              <button
                ref={exchangesBtnRef}
                type='button'
                onMouseDown={handleExchangesToggle}
                className={classnames('sp-exchanges-btn', {
                  'sp-exchanges-btn--active': isExchangesOpen || selectedExchanges.length < exchanges.length,
                })}
              >
                Exchanges {selectedExchanges.length < exchanges.length && `(${selectedExchanges.length})`}
              </button>
              <OptionsSelectPopover
                id='sp-exchanges-popover'
                isOpen={isExchangesOpen}
                coords={popoverCoords}
                mode='multi'
                title='Filter Exchanges'
                options={exchanges.map(ex => ({id: ex, label: ex}))}
                selectedIds={selectedExchanges}
                onSelect={toggleExchange}
                primaryAction={{
                  label: 'All',
                  onClick() {
                    setSelectedExchanges(exchanges);
                  },
                }}
                secondaryAction={{
                  label: 'None',
                  onClick() {
                    setSelectedExchanges([]);
                  },
                }}
              />
            </div>

            {showSelectionStatus && (
              <div className='sp-selection-status'>
                Selected Products ({localSelection.length})
              </div>
            )}

            <div
              className={classnames('sp-results', {
                /* Reserve enough vertical space to show the table header plus one
                 * data row whenever the search has returned at least one instrument. */
                'sp-results--has-data-rows': searchTerm.length > 0
                  && !isSymbolSearchLoading
                  && sortedSymbols.length > 0,
              })}
            >
              {searchTerm.length === 0
                ? (
                  <div className='sp-empty-state'>
                    Begin typing to search for symbols
                  </div>
                )
                : (isSymbolSearchLoading
                  ? (
                    <div className='sp-loading'>
                      <Spinner size='normal' />
                    </div>
                  )
                  : (
                    <table className='sp-table'>
                      <thead className='sp-thead'>
                        <tr>
                          {showSelectionStatus && <th className='sp-th sp-th--checkbox' />}
                          <th className='sp-th sp-th--symbol'>
                            <div className='sp-th-inner'>
                              <SortButton keyName='symbol' sortConfig={sortConfig} setSortConfig={setSortConfig} />
                            </div>
                          </th>
                          <th className='sp-th sp-th--normalized'>
                            <div className='sp-th-inner'>
                              <SortButton keyName='normalizedSymbol' sortConfig={sortConfig} setSortConfig={setSortConfig} />
                            </div>
                          </th>
                          <th className='sp-th sp-th--exchange'>
                            <div className='sp-th-inner'>
                              <SortButton keyName='segmentMic' sortConfig={sortConfig} setSortConfig={setSortConfig} />
                            </div>
                          </th>
                          <th className='sp-th'>
                            <div className='sp-th-inner'>
                              <SortButton keyName='description' sortConfig={sortConfig} setSortConfig={setSortConfig} />
                            </div>
                          </th>
                          <th className='sp-th sp-th--type'>
                            <div className='sp-th-inner sp-th-inner--right'>
                              <SortButton keyName='secType' sortConfig={sortConfig} setSortConfig={setSortConfig} />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSymbols.map(product => {
                          const isSelected = localSelection.includes(product);
                          return (
                            <tr
                              key={product.symbol}
                              className={classnames('sp-row', {'sp-row--selected': isSelected && showSelectionStatus})}
                              onClick={() => {
                                handleRowClick(product);
                              }}
                            >
                              {showSelectionStatus && (
                                <td className='sp-cell sp-cell--checkbox'>
                                  <input
                                    type='checkbox'
                                    checked={isSelected}
                                    readOnly
                                    className='sp-checkbox'
                                  />
                                </td>
                              )}
                              <td className='sp-cell sp-cell--symbol'>
                                <TruncatedText text={product.symbol ?? ''} />
                              </td>
                              <td className='sp-cell'>
                                <TruncatedText text={product.normalizedSymbol ?? ''} />
                              </td>
                              <td className='sp-cell sp-cell--strong'>
                                <TruncatedText text={product.segmentMic ?? ''} />
                              </td>
                              <td className='sp-cell'>
                                <TruncatedText text={product.description ?? ''} />
                              </td>
                              <td className='sp-cell sp-cell--type'>
                                <TruncatedText text={secTypeLabel(product.secType)} align='right' />
                              </td>
                            </tr>
                          );
                        })}
                        {sortedSymbols.length === 0 && (
                          <tr>
                            <td colSpan={tableSpan} className='sp-no-results'>
                              No instruments match your search criteria
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ))}
            </div>

            {hasExplicitApply && isMultiMode && (
              <div className='sp-dropdown-footer-slot'>
                {renderFooter?.({
                  selected: localSelection,
                  clear: clearSelection,
                  apply: applySelection,
                  close: closeDropdown,
                })}
              </div>
            )}
          </div>
        </div>,
        searchRef.current?.ownerDocument?.body ?? document.body,
      )}
    </div>
  );
};
/* eslint-enable complexity */

export default SymbolPicker;
